import { ActionDto, ConversationActionDto } from "@coaching-culture/types";
import {
  Circle,
  EmptyState,
  Flex,
  IconButton,
  IconToast,
  Label,
  Loader,
  MultiSelect,
  Panel,
  Table,
  formatDate,
  useToast,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { ScrollableContainer } from "components/ScrollableContainer";
import { Toggle } from "components/Toggle";
import { endOfDay, fromUnixTime, isPast } from "date-fns";
import { sortBy, uniq } from "lodash";
import { useRemoveAction, useUserActions } from "queries/actions";
import { useChangeActionItem } from "queries/conversations";
import { useState } from "react";
import {
  FaBolt,
  FaBullhorn,
  FaCalendarAlt,
  FaCheckSquare,
  FaClipboardList,
  FaExclamationTriangle,
  FaEye,
  FaHandshake,
  FaThumbsUp,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { useLocalStorage } from "react-use";
import styled, { useTheme } from "styled-components";
import { grid, GridProps } from "styled-system";
import { clientUrlForContent } from "utils/content";
import { WidgetPanelHeader } from "./WidgetPanelHeader";

const Widget = styled(Panel)<GridProps>`
  ${grid};
`;

type ActionCategory = "conversations" | "feedback" | "goals" | "learning";

const labelProps: Record<
  ActionDto["type"],
  { category: ActionCategory; children: string }
> = {
  requestfeedback: {
    category: "feedback",
    children: "Get feedback",
  },
  conversation: {
    category: "conversations",
    children: "Conversation",
  },
  providefeedback: {
    category: "feedback",
    children: "Give feedback",
  },
  survey: {
    category: "feedback",
    children: "Survey",
  },
  assessment: {
    category: "learning",
    children: "Assessment",
  },
  measures: {
    category: "goals",
    children: "Update measures",
  },
  contfeedback: {
    category: "feedback",
    children: "Give user feedback",
  },
};

const TypeLabel = ({ type }: { type: ActionDto["type"] }) => {
  const theme = useTheme();
  const { category, children } = labelProps[type];

  return (
    <Label
      size="small"
      color={theme.colors[`${category}Soft`]}
      textColor={theme.colors[`${category}Text`]}
    >
      {children}
    </Label>
  );
};

function isOutstanding(item: ActionDto) {
  return item.completedOn == null;
}

function isActionOverdue(item: ActionDto) {
  const dueDate = "dueDate" in item ? item.dueDate : null;

  return (
    dueDate != null &&
    item.completedOn == null &&
    isPast(endOfDay(fromUnixTime(dueDate)))
  );
}

const justMine = (who: string) => (item: ActionDto) => {
  if (item.type === "conversation") {
    return item.assignedTo.id === who;
  }
  return true;
};
export const ActionsWidget = () => {
  const [user] = useUser();
  const [showAll, setShowAll] = useLocalStorage("action-widget-all", false);
  const history = useHistory();
  const actions = useUserActions(user.id);
  const modifyAction = useChangeActionItem();
  const removeAction = useRemoveAction();
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const toast = useToast();

  let toShow = showAll
    ? (actions.data ?? [])
    : (actions.data ?? []).filter(isOutstanding);

  const getActionText = (item: ActionDto): string => {
    if (item.type === "conversation") {
      return item.text;
    } else if (item.type === "survey") {
      return `Complete survey`;
    } else if (item.type === "providefeedback") {
      return `Please give ${item.campaign.user.name} some feedback`;
    } else if (item.type === "requestfeedback") {
      return "Time to get some feedback...";
    } else if (item.type === "assessment") {
      return `Complete assessment: ${item.content.name}`;
    } else if (item.type === "measures") {
      return "Update your team's measures";
    } else if (item.type === "contfeedback") {
      return `Give ${item.requester.name} some feedback`;
    }
  };

  const getActionItem = (item: ActionDto): React.ReactNode | null => {
    if (item.type === "conversation") {
      return "N/A";
    } else if (item.type === "survey") {
      return (
        <Flex alignItems={"center"}>
          <Circle icon={FaClipboardList} mr={1} color="primary" />
          <Link to={`/solutions/surveys/${item.survey.id}`}>
            {item.survey.name}
          </Link>
        </Flex>
      );
    } else if (item.type === "providefeedback") {
      return (
        <Flex alignItems={"center"}>
          <Circle icon={FaUser} mr={1} color="primary" />
          <Link to={`/feedback?survey=${item.code}`}>
            {item.campaign.user.name}
          </Link>
        </Flex>
      );
    } else if (item.type === "requestfeedback") {
      return (
        <Flex alignItems={"center"}>
          <Circle icon={FaHandshake} mr={1} color="primary" />
          <Link to={`/solutions/feedback/campaigns/${item.campaign.id}`}>
            {item.campaign.name}
          </Link>
        </Flex>
      );
    } else if (item.type === "assessment") {
      return (
        <Flex alignItems={"center"}>
          <Circle icon={FaEye} mr={1} color="primary" />
          <Link to={`/solutions/assessments/modules/${item.content.slug}`}>
            {item.content.name}
          </Link>
        </Flex>
      );
    } else if (item.type === "measures") {
      return "N/A";
    } else if (item.type === "contfeedback") {
      return (
        <Flex alignItems={"center"}>
          <Circle icon={FaBullhorn} mr={1} color="primary" />
          <Link to={`/solutions/continuous-feedback/view/${item.requestId}`}>
            Provide feedback
          </Link>
        </Flex>
      );
    }
  };

  const getDueDate = (item: ActionDto): React.ReactNode => {
    const date: number | null = "dueDate" in item ? item.dueDate : null;

    if (date == null) {
      return (
        <Label size="small" icon={FaCalendarAlt}>
          No due date
        </Label>
      );
    }

    const overdue = isActionOverdue(item);

    return (
      <Label
        color={overdue ? "danger" : undefined}
        size="small"
        icon={overdue ? FaExclamationTriangle : FaCalendarAlt}
        title={overdue ? "This action is overdue" : undefined}
      >
        {formatDate(date)}
        {overdue && " · overdue"}
      </Label>
    );
  };

  const toggleAction = async (item: ConversationActionDto) => {
    await modifyAction.mutateAsync({
      value: item.completedOn == null,
      conversationId: item.conversation.id,
      actionItemId: item.id,
    });
    toast({
      content: (
        <IconToast
          icon={FaThumbsUp}
          iconColor="primary"
          text="Action updated"
        />
      ),
    });
  };

  const delAction = async (item: ConversationActionDto) => {
    if (window.confirm("Are you sure you want to delete this action?")) {
      await removeAction.mutateAsync(item.id);

      toast({
        content: (
          <IconToast icon={FaTrash} iconColor="primary" text="Action removed" />
        ),
      });
    }
  };

  const getInteractions = (item: ActionDto) => {
    if (item.type === "conversation") {
      return (
        <>
          <IconButton
            icon={FaCheckSquare}
            color={item.completedOn == null ? "body" : "positive"}
            onClick={() => toggleAction(item)}
          />
          <IconButton
            icon={FaTrash}
            color="danger"
            onClick={() => delAction(item)}
          />
          <IconButton
            icon={FaEye}
            color="primary"
            onClick={() =>
              history.push(
                `/solutions/performance/conversations/${item.conversation.id}/${item.conversation.index}`,
              )
            }
          />
        </>
      );
    } else if (item.type === "measures") {
      return (
        <IconButton
          icon={FaEye}
          color="primary"
          onClick={() => {
            history.push("/solutions/my-team");
          }}
        />
      );
    } else if (item.type === "assessment") {
      return (
        <IconButton
          icon={FaEye}
          color="primary"
          onClick={() => {
            history.push(clientUrlForContent(item.content));
          }}
        />
      );
    } else if (item.type === "survey") {
      return (
        <IconButton
          icon={FaEye}
          color="primary"
          onClick={() => {
            history.push(`/solutions/surveys/${item.survey.id}`);
          }}
        />
      );
    } else if (item.type === "providefeedback") {
      return (
        <IconButton
          icon={FaEye}
          color="primary"
          onClick={() => history.push(`/feedback?survey=${item.code}`)}
        />
      );
    } else if (item.type === "requestfeedback") {
      return (
        <IconButton
          icon={FaEye}
          color="primary"
          onClick={() =>
            history.push(`/solutions/feedback/campaigns/${item.campaign.id}`)
          }
        />
      );
    } else if (item.type === "contfeedback" && item.completedOn == null) {
      return (
        <IconButton
          icon={FaEye}
          color="primary"
          onClick={() =>
            history.push(
              `/solutions/continuous-feedback/send/${item.requester.id}/${item.requestId}`,
            )
          }
        />
      );
    }
  };

  const typeOptions = uniq(toShow.map((x) => x.type)).map((x) => ({
    label: labelProps[x].children,
    value: x,
  }));

  if (typeFilter.length > 0) {
    toShow = toShow.filter((x) => typeFilter.includes(x.type));
  }

  return (
    <Widget
      gridColumn={["span 1", "span 1", "span 1", "span 2"]}
      width={"100%"}
    >
      <WidgetPanelHeader title="Actions">
        <MultiSelect
          options={typeOptions}
          value={typeFilter}
          onChange={setTypeFilter}
          defaultLabel="Filter by type"
        />
        <Flex border={1} px={1} borderRadius={3}>
          <Toggle
            ariaLabel="Display all actions"
            leftLabel="Outstanding"
            rightLabel="All"
            value={showAll}
            onChange={setShowAll}
          />
        </Flex>
      </WidgetPanelHeader>
      {actions.isLoading ? (
        <Loader />
      ) : actions.isError ? (
        <EmptyState compact icon={FaBolt}>
          Couldn't load your actions. Refresh to try again.
        </EmptyState>
      ) : toShow.length === 0 ? (
        <EmptyState compact icon={FaBolt}>
          You have no outstanding actions.
        </EmptyState>
      ) : (
        <ScrollableContainer maxHeight={310}>
          <Table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Action</th>
                <th>Item</th>
                <th>Due date</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sortBy(toShow.filter(justMine(user.id)), "date").map((x, i) => (
                <tr key={i}>
                  <td>
                    <TypeLabel type={x.type} />
                  </td>
                  <td>{getActionText(x)}</td>
                  <td>{getActionItem(x)}</td>
                  <td>{getDueDate(x)}</td>
                  <td>
                    <Flex
                      alignItems="center"
                      justifyContent={"flex-end"}
                      style={{ gap: 12 }}
                    >
                      {getInteractions(x)}
                    </Flex>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ScrollableContainer>
      )}
    </Widget>
  );
};
